import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Chip,
  FormControlLabel,
  Radio,
  Autocomplete,
} from "@mui/material";
import { Controller } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useDialog } from "../../../../components/DialogContext";
import { LoadingButton } from "@mui/lab";
import { useDispatch, useSelector } from "react-redux";
import { getPracticeArea } from "../../../../redux/Apps/Lawyerapp/CaseSlice/CaseSlice";
import { backend_api } from "../../../../static/server";
import {
  createTaskList,
  getAllTaskList,
  updateTaskList,
} from "../../../../redux/Apps/Lawyerapp/TaskSlice/TaskSlice";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import CreateTaskTemplateForm from "./CreateTaskTemplateForm";
import empty_list from "../../../../assets/empty-todos.webp";
import TaskApiField from "../../../../components/LawyerCompo/Settings/FieldsUi/TaskApiField";

const CreateTaskListForm = ({ isEditMode, formData }) => {
  const { closeDialog } = useDialog();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { allPracticeArea, loading: practiceAreaLoading } = useSelector(
    (state) => state.case
  );
  const { token, teamMemberLawyerId } = useSelector((data) => data.user);

  const [selected, setSelected] = useState(null);
  const [selectedPracticeArea, setSelectedPracticeArea] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      task_list_name: "",
      is_practice_area: false,
      is_people: false,
      is_company: false,
      extra: {
        description: "",
        practice_area: null,
      },
      tasks: [],
    },
  });

  const watchedTasks = watch("tasks");
  const extractTaskTemplateId = (task) => {
    if (task === null || task === undefined) return null;

    // If it's already a number or string, return as-is
    if (typeof task === "number" || typeof task === "string") {
      return task;
    }

    // If it's an object, extract the ID
    if (typeof task === "object") {
      return (
        task.id ??
        task.task_template ??
        task.template_id ??
        task.task_template_id ??
        task.value ??
        null
      );
    }

    return null;
  };

  useEffect(() => {
    dispatch(
      getPracticeArea({
        url: `${backend_api}case/case-type/?case_service_type=practice_area`,
        token: token,
      })
    );

    dispatch(
      getAllTaskList({
        url: `${backend_api}task/task-list/`,
        token: token,
        teamMemberLawyerId: teamMemberLawyerId,
      })
    );
  }, [dispatch, token, teamMemberLawyerId]);

  useEffect(() => {
    if (isEditMode && formData) {
      setValue("task_list_name", formData?.task_list_name);
      setValue("extra.description", formData?.extra?.description || "");
      setValue("extra.practice_area", formData?.extra?.practice_area || null);
      setValue("is_practice_area", formData?.is_practice_area || false);
      setValue("is_people", formData?.is_people || false);
      setValue("is_company", formData?.is_company || false);

      // Handle tasks data - store as array of IDs
      const taskData = Array.isArray(formData?.tasks) ? formData.tasks : [];
      const normalizedTasks = taskData
        .map((task) => extractTaskTemplateId(task))
        .filter((id) => id !== null && id !== undefined);

      // Set tasks as array of IDs
      setValue("tasks", normalizedTasks);

      // Set category and practice area
      if (formData?.extra?.practice_area) {
        const foundArea = allPracticeArea?.find(
          (area) => area.id === formData.extra.practice_area
        );
        setSelectedPracticeArea(foundArea);
        setSelected("is_practice_area");
      } else if (formData?.is_people) {
        setSelected("is_people");
      } else if (formData?.is_company) {
        setSelected("is_company");
      }
    }
  }, [isEditMode, formData, allPracticeArea, setValue]);

  const handleChange = (event) => {
    const selectedValue = event.target.value;
    setSelected(selectedValue);

    setValue("is_practice_area", false);
    setValue("is_people", false);
    setValue("is_company", false);

    setValue(selectedValue, true);

    setSelectedPracticeArea(null);
    setValue("extra.practice_area", null);

    // Clear tasks when category changes to refresh the dropdown with new filters
    setValue("tasks", []);
  };

  // Generate filter parameters based on selected category
  const getTaskTemplateFilters = () => {
    const filters = {};

    if (selected === "is_practice_area") {
      filters.is_practice_area = true;
      if (selectedPracticeArea) {
        filters.practice_area = selectedPracticeArea.id;
      }
    } else if (selected === "is_people") {
      filters.is_people = true;
    } else if (selected === "is_company") {
      filters.is_company = true;
    }

    return filters;
  };

  const getTaskTemplateApiUrl = () => {
    const baseUrl = `${backend_api}task/task-template/`;
    const filters = getTaskTemplateFilters();

    if (Object.keys(filters).length === 0) {
      return baseUrl;
    }

    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      queryParams.append(key, value);
    });

    return `${baseUrl}?${queryParams.toString()}`;
  };

  const selectedCategoryLabel =
    selected === "is_practice_area"
      ? "Practice Area"
      : selected === "is_people"
      ? "People"
      : selected === "is_company"
      ? "Company"
      : "selected category";

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      let tasksForSubmission = [];
      if (Array.isArray(data.tasks)) {
        tasksForSubmission = data.tasks
          .map((task) => extractTaskTemplateId(task))
          .filter((id) => id !== null && id !== undefined);
      }

      const submissionData = {
        ...data,
        tasks: tasksForSubmission,
      };


      if (isEditMode) {
        await dispatch(
          updateTaskList({
            url: `${backend_api}task/task-list/${formData?.id}/`,
            body: submissionData,
            token: token,
          })
        ).unwrap();
        toast.success("Task list updated successfully");
      } else {
        await dispatch(
          createTaskList({
            url: `${backend_api}task/task-list/`,
            body: submissionData,
            token: token,
          })
        ).unwrap();
        toast.success("Task template created successfully");
      }

      await dispatch(
        getAllTaskList({
          url: `${backend_api}task/task-list/`,
          token: token,
          teamMemberLawyerId: teamMemberLawyerId,
        })
      ).unwrap();
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(
        isEditMode
          ? "Failed to update Task template"
          : "Failed to create Task template"
      );
    } finally {
      setIsSubmitting(false);
      closeDialog();
      reset();
    }
  };

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "1.5rem",
            p: "1rem",
          }}
        >
          {/* Task Name */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Controller
                name="task_list_name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    label={
                      <span>
                        Group Name <span style={{ color: "red" }}>*</span>
                      </span>
                    }
                    placeholder="Enter group name"
                    error={!!errors?.task_list_name}
                    helperText={
                      errors?.task_list_name ? "This field is required" : null
                    }
                  />
                )}
              />
            </Box>
          </motion.div>

          {/* Category Selection */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#333" }}
              >
                Select Category
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "start",
                  alignItems: "center",
                  gap: "1rem",
                  flexWrap: "wrap",
                }}
              >
                {[
                  { key: "is_practice_area", label: "Practice Area" },
                  { key: "is_people", label: "People" },
                  { key: "is_company", label: "Company" },
                ].map((opt, index) => (
                  <motion.div
                    key={opt.key}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Chip
                      label={
                        <FormControlLabel
                          value={opt.key}
                          control={
                            <Radio
                              checked={selected === opt.key}
                              onChange={handleChange}
                              value={opt.key}
                            />
                          }
                          label={opt.label}
                        />
                      }
                      variant="outlined"
                      sx={{
                        transition: "all 0.3s ease",
                      }}
                    />
                  </motion.div>
                ))}
              </Box>
            </Box>
          </motion.div>

          {/* Practice Area Autocomplete */}
          <AnimatePresence mode="wait">
            {selected === "is_practice_area" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                <Controller
                  name="practice_area"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      size="small"
                      options={allPracticeArea || []}
                      getOptionLabel={(option) => option?.name || ""}
                      value={selectedPracticeArea}
                      onChange={(event, newValue) => {
                        setSelectedPracticeArea(newValue);
                        setValue("practice_area", newValue?.id || null);
                        setValue("tasks", []);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Practice Area"
                          placeholder="Choose a practice area..."
                          fullWidth
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {option.name}
                            </Typography>
                            {option.description && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {option.description.length > 60
                                  ? `${option.description.substring(0, 60)}...`
                                  : option.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                      loading={practiceAreaLoading}
                      loadingText="Loading practice areas..."
                      noOptionsText="No practice areas found"
                    />
                  )}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Task Template Selection - Now with Filtering */}
          {selected && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <TaskApiField
                  name="tasks"
                  label="Select Task Templates to Create Group"
                  control={control}
                  errors={errors}
                  token={token}
                  apiUrl={getTaskTemplateApiUrl()}
                  placeholder="Choose multiple task templates..."
                  multiple={true}
                  returnFullData={false}
                  teamMemberLawyerId={teamMemberLawyerId}
                  TaskTypeFormComponent={CreateTaskTemplateForm}
                  createTaskTypeDialogTitle="Add Task template"
                  searchKey="task_name__icontains"
                  addButtonText="Add Task template"
                  noDataText={`Don't have any task template for ${selectedCategoryLabel}`}
                  noDataButtonText="Task template"
                  noDataImage={empty_list}
                  idKey="id"
                  filters={getTaskTemplateFilters()}
                />

                {/* Show selected templates count */}
                {watchedTasks &&
                  Array.isArray(watchedTasks) &&
                  watchedTasks.length > 0 && (
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{ mt: 0.5 }}
                    >
                      {watchedTasks.length} task template
                      {watchedTasks.length !== 1 ? "s" : ""} selected
                      {selected && (
                        <span style={{ color: "#666", marginLeft: "8px" }}>
                          (filtered by{" "}
                          {selected === "is_practice_area"
                            ? "Practice Area"
                            : selected === "is_people"
                            ? "People"
                            : "Company"}
                          )
                        </span>
                      )}
                    </Typography>
                  )}
              </Box>
            </motion.div>
          )}

          {/* Show message when no category is selected */}
          {!selected && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.9rem" }}
              >
                Please select a category first to view task templates
              </Typography>
            </motion.div>
          )}

          {/* Description */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
          >
            <Box>
              <Controller
                name="extra.description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    multiline
                    rows={3}
                    fullWidth
                    label="Description"
                    placeholder="Enter task template description..."
                  />
                )}
              />
            </Box>
          </motion.div>
        </Box>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.5 }}
        >
          <Box
            sx={{
              height: "fit-content",
              borderTop: "1px solid #ddd",
              position: "sticky",
              bottom: 0,
              backdropFilter: "blur(10px)",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              p: "0.5rem",
              zIndex: 1000,
              gap: "0.5rem",
              bgcolor: "rgba(255, 255, 255, 0.8)",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => closeDialog()}
            >
              Cancel
            </Button>
            <LoadingButton
              loading={isSubmitting}
              variant="contained"
              color="primary"
              type="submit"
              size="small"
            >
              {isEditMode ? "Update Group" : "Create Group"}
            </LoadingButton>
          </Box>
        </motion.div>
      </form>
    </Box>
  );
};

export default CreateTaskListForm;
